import {
  ChannelGroupingKey,
  Subdimension,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { GridColDef } from '@mui/x-data-grid';
import { ValueInput, formatters } from '@utils/formattersV2';
import {
  nameColumn,
  renderCellStatisticallySignificant,
  renderHeaderSales,
} from './columns';
import { channelNames } from '@features/campaigns/subdomains/visitation/components/Summary/SummaryView/Sales/SalesMetrics';

const salesLiftPartFormatter = (value: ValueInput) =>
  formatters.percentage(value, { decimals: 1 });

const metadataByChannel = {
  total: {
    fieldSuffix: 'Overall',
    label: channelNames.total,
  },
  inStore: {
    fieldSuffix: 'InStore',
    label: channelNames.inStore,
  },
  online: {
    fieldSuffix: 'Online',
    label: channelNames.online,
  },
} as const satisfies Record<
  ChannelGroupingKey,
  {
    fieldSuffix: string;
    label: string;
  }
>;

const salesLiftColumnsFromChannel = (
  channel: ChannelGroupingKey
): GridColDef<Subdimension, ValueInput, string>[] => [
  {
    field: `transactionSizeLift${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Transaction Size Lift - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.transactionSizeLift[channel],
    valueFormatter: salesLiftPartFormatter,
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
  {
    field: `transactionRateLift${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Transaction Rate Lift - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.transactionRateLift[channel],
    valueFormatter: salesLiftPartFormatter,
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
  {
    field: `penetrationLift${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Penetration Lift - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.penetrationLift[channel],
    valueFormatter: salesLiftPartFormatter,
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
];

const exposedColumnsFromChannel = (
  channel: ChannelGroupingKey
): GridColDef<Subdimension, ValueInput, string>[] => [
  {
    field: `exposedTransactionSize${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Exposed Transaction Size - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.exposedTransactionSize[channel],
    valueFormatter: value => formatters.currency(value, { decimals: 2 }),
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
  {
    field: `exposedTransactionRate${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Exposed Transaction Rate - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.exposedTransactionRate[channel],
    valueFormatter: value => formatters.number(value, { decimals: 1 }),
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
  {
    field: `exposedPenetration${metadataByChannel[channel].fieldSuffix}`,
    headerName: `Exposed Penetration - ${metadataByChannel[channel].label}`,
    flex: 1,
    valueGetter: (_, row) => row.salesMetrics?.exposedPenetration[channel],
    valueFormatter: value => formatters.percentage(value, { decimals: 1 }),
    renderHeader: renderHeaderSales,
    renderCell: renderCellStatisticallySignificant,
  },
];

export const salesContributionMetricsColumns =
  (): GridColDef<Subdimension>[] => [
    nameColumn,
    ...Object.keys(metadataByChannel).flatMap(channel =>
      salesLiftColumnsFromChannel(channel as ChannelGroupingKey)
    ),
    ...Object.keys(metadataByChannel).flatMap(channel =>
      exposedColumnsFromChannel(channel as ChannelGroupingKey)
    ),
  ];
